import { Repeater, types } from "react-bricks/frontend";
import styled from "styled-components";
import { color } from "@styles";
import { EventsBarItemProps } from "./EventsBarItem";
import { restrictedBackgroundColorProp } from "../utils/backgroundColor";
import { verticalPadSideProp } from "../utils/verticalPadding";

export interface EventsBarProps {
  backgroundColor?: types.IColor;
  barColor?: types.IColor;
  blockStyle: "block" | "rounded";
  blocks: types.RepeaterItems<EventsBarItemProps>;
  textColor?: types.IColor;
}

export const EventsBar: types.Brick<EventsBarProps> = ({
  backgroundColor,
  barColor,
  blocks = [],
  blockStyle = "block",
  textColor = { color: color.white },
}) => {
  const { margin, borderRadius, padding, height } = getPropsFromStyle(blockStyle);

  return (
    <Wrapper backgroundColor={backgroundColor?.color}>
      <Container
        backgroundColor={barColor?.color}
        margin={margin}
        borderRadius={borderRadius}
        padding={padding}
        height={height}
      >
        <Repeater
          propName="blocks"
          items={blocks}
          renderItemWrapper={(item, i) => (
            <>
              {i > 0 && <Line color={textColor.color} />}
              {item}
            </>
          )}
          itemProps={{ textColor: textColor }}
        />
      </Container>
    </Wrapper>
  );
};

const getPropsFromStyle = (c: string) => {
  switch (c) {
    case "block":
      return {
        margin: "0",
        borderRadius: "0",
        padding: "5rem 10rem",
        height: "12.5rem",
      };
    case "rounded":
      return {
        margin: "5rem",
        borderRadius: "4.5rem",
        padding: "1.5rem 3rem",
        height: "5.5rem",
      };
  }
};

EventsBar.schema = {
  name: "events-bar",
  label: "Events Bar",
  category: "Events Bar Components",
  previewImageUrl: "/static/bricks/events-bar.png",
  repeaterItems: [
    {
      name: "blocks",
      itemType: "events-bar-block",
      itemLabel: "Block",
      min: 1,
      max: 3,
    },
  ],
  // Define default props
  getDefaultProps: () => ({
    backgroundColor: { color: color.white },
    barColor: { color: color.S2100 },
    blockStyle: "block",
    blocks: [
      {
        hasIcon: false,
        iconImage: {
          src: "",
        },
        description: "Live Webinar",
        textColor: {
          color: color.white,
        },
      },
      {
        hasIcon: true,
        iconImage: {
          src: "/static/icons/Event.svg",
        },
        description: "Thursday 7th November",
        textColor: {
          color: color.white,
        },
      },
      {
        hasIcon: true,
        iconImage: {
          src: "/static/icons/Clock.svg",
        },
        description: "10am SAST",
        textColor: {
          color: color.white,
        },
      },
    ],
  }),
  // Define side edit props
  sideEditProps: [
    verticalPadSideProp,
    restrictedBackgroundColorProp,
    {
      name: "barColor",
      label: "Bar Color",
      type: types.SideEditPropType.Select,
      shouldRefreshStyles: true,
      selectOptions: {
        display: types.OptionsDisplay.Color,
        options: [
          { value: { color: color.P100 }, label: "Pink" },
          { value: { color: color.S2100 }, label: "Blue" },
          { value: { color: color.S3100 }, label: "Purple" },
          { value: { color: color.S4100 }, label: "Yellow" },
          { value: { color: "#00bd7dff" }, label: "Green" },
        ],
      },
    },
    {
      name: "blockStyle",
      label: "Block Style",
      type: types.SideEditPropType.Select,
      selectOptions: {
        display: types.OptionsDisplay.Radio,
        options: [
          { value: "block", label: "Block" },
          { value: "rounded", label: "Rounded" },
        ],
      },
    },
    {
      name: "textColor",
      label: "Text Color",
      type: types.SideEditPropType.Select,
      shouldRefreshStyles: true,
      selectOptions: {
        display: types.OptionsDisplay.Color,
        options: [
          { value: { color: color.white }, label: "White" },
          { value: { color: color.B1100 }, label: "Black" },
        ],
      },
    },
  ],
};

const Wrapper = styled.section<{
  backgroundColor: string;
}>`
  background: ${(p) => p.backgroundColor || color.white};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  @media only screen and (max-width: 65rem) {
    padding: 0;
  }
`;

const Container = styled.div<{
  backgroundColor: string;
  margin: string;
  borderRadius: string;
  padding: string;
  height: string;
}>`
  background: ${(p) => p.backgroundColor};
  margin: ${(p) => p.margin};
  border-radius: ${(p) => p.borderRadius};
  padding: ${(p) => p.padding};
  height: ${(p) => p.height};
  justify-items: center;
  align-items: center;
  overflow: hidden;
  display: flex;
  justify-content: space-between;
  overflow: visible;

  @media only screen and (max-width: 65rem) {
    flex-direction: column;
    height: auto;
    padding: 2.5rem 0 5rem;
    border-radius: 0;
    margin: 0;
  }
`;

const Line = styled.div<{
  color: string;
}>`
  height: 2.5rem;
  border-left: ${(p) => `1px solid ${p.color}`};

  @media only screen and (max-width: 65rem) {
    height: 1px;
    border-left: none;
    border-bottom: ${(p) => `1px solid ${p.color}`};
    width: 50%;
    padding: 1rem 0;
  }
`;

export default EventsBar;
